
import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, X, Sparkles, AlertCircle, FileText, ChevronDown, Minimize2, Maximize2 } from 'lucide-react';
import { User, Document, DocumentType } from '../types';
import { useTranslation } from 'react-i18next';
import { askFinancialAdvisor } from '../services/ai-service';
import { getFinancialDocuments } from '../utils/financialUtils';

interface AiAssistantProps {
    currentUser: User;
    targetUser: User;
    agencyName?: string;
    documents: Document[];
    documentTypes: DocumentType[];
}

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    sources?: Document[]; // Citations
    timestamp: number;
    isThinking?: boolean;
}

export const AiAssistant: React.FC<AiAssistantProps> = ({ currentUser, targetUser, agencyName, documents, documentTypes }) => {
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: 'welcome',
            role: 'assistant',
            content: `Hello! I am ${agencyName || 'your'} AI Advisor. I can analyze your uploaded documents perfectly. Ask me anything about your invoices, expenses, or financial data.`,
            timestamp: Date.now()
        }
    ]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [activeTab, setActiveTab] = useState<'chat' | 'shortcuts'>('chat');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    // Check if feature is enabled
    const isEnabled = targetUser.config?.ai_features?.document_ai_enabled;

    if (!isEnabled) return null;

    const handleSend = async (text?: string) => {
        const messageContent = typeof text === 'string' ? text : input;
        if (!messageContent.trim() || isThinking) return;

        // Switch to chat if not already
        setActiveTab('chat');

        const userMsg: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: messageContent.trim(),
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsThinking(true);

        try {
            // Call AI Service
            // API Key is now handled by the backend proxy.

            // Filter documents to ensure consistency with Financial Overview
            const financialDocs = getFinancialDocuments(documents, documentTypes);

            const response = await askFinancialAdvisor(userMsg.content, financialDocs);

            const aiMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: response.answer,
                sources: response.sources as Document[],
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, aiMsg]);

        } catch (error) {
            console.error("AI Error:", error);
            const errorMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: "I apologize, but I encountered an error while analyzing your documents. Please try again.",
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsThinking(false);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const SUGGESTED_QUESTIONS = [
        "Ποιες είναι οι οφειλές μου προς το κράτος έως το τέλος του μήνα;",
        "Ποιες είναι οι οφειλές μου προς το κράτος τον επόμενο μήνα;",
        "Τί ΦΠΑ έχω να πληρώσω;",
        "Τί ΕΦΚΑ έχω να πληρώσω;"
    ];

    // Minimized View (Floating Bubble)
    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-6 right-6 w-14 h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-xl flex items-center justify-center transition-all z-50 animate-bounce-subtle"
                title="Ask AI Advisor"
            >
                <Bot size={28} />
                <span className="absolute top-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></span>
            </button>
        );
    }

    return (
        <div className={`fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col transition-all z-50 ${isMinimized ? 'w-72 h-14 overflow-hidden' : 'w-96 h-[600px] max-h-[80vh]'}`}>
            {/* Header */}
            <div
                className="bg-purple-600 p-4 flex items-center justify-between cursor-pointer rounded-t-2xl"
                onClick={() => setIsMinimized(!isMinimized)}
            >
                <div className="flex items-center gap-2 text-white">
                    <div className="bg-white/20 p-1.5 rounded-lg">
                        <Sparkles size={18} />
                    </div>
                    <div>
                        <h3 className="font-bold text-sm">AI Advisor</h3>
                        <p className="text-[10px] text-purple-200">Strict Document Analysis</p>
                    </div>
                </div>
                <div className="flex items-center gap-1 text-purple-200">
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                        className="p-1 hover:bg-white/10 rounded"
                    >
                        {isMinimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                        className="p-1 hover:bg-white/10 rounded hover:text-white"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            {!isMinimized && (
                <div className="flex border-b border-slate-100">
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'chat' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Chat
                    </button>
                    <button
                        onClick={() => setActiveTab('shortcuts')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'shortcuts' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Shortcuts
                    </button>
                </div>
            )}

            {/* Chat Area */}
            {!isMinimized && activeTab === 'chat' && (
                <>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] rounded-2xl p-3 ${msg.role === 'user'
                                    ? 'bg-purple-600 text-white rounded-tr-none'
                                    : 'bg-white border border-slate-200 text-slate-700 rounded-tl-none shadow-sm'
                                    }`}>
                                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>

                                    {/* Sources / Citations */}
                                    {msg.sources && msg.sources.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">References:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {msg.sources.map(doc => (
                                                    <a
                                                        key={doc.id}
                                                        href={doc.fileUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="flex items-center gap-1 text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded-md transition-colors"
                                                    >
                                                        <FileText size={10} />
                                                        <span className="truncate max-w-[100px]">{doc.title}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <p className={`text-[10px] mt-1 text-right ${msg.role === 'user' ? 'text-purple-200' : 'text-slate-400'}`}>
                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}

                        {isThinking && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-4 shadow-sm flex items-center gap-2">
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-75"></div>
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-150"></div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-3 bg-white border-t border-slate-100">
                        <div className="relative flex items-center">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask about invoices, totals, dates..."
                                className="w-full bg-slate-100 text-slate-800 rounded-xl pl-4 pr-12 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500 transition-all placeholder:text-slate-400"
                                disabled={isThinking}
                            />
                            <button
                                onClick={() => handleSend()}
                                disabled={!input.trim() || isThinking}
                                className={`absolute right-2 p-1.5 rounded-lg transition-colors ${input.trim() && !isThinking
                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                    }`}
                            >
                                <Send size={16} />
                            </button>
                        </div>
                        <div className="text-center mt-2">
                            <p className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
                                <AlertCircle size={10} /> AI can make mistakes. Check important info.
                            </p>
                        </div>
                    </div>
                </>
            )}

            {/* Shortcuts View */}
            {!isMinimized && activeTab === 'shortcuts' && (
                <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Quick Questions</p>
                    <div className="flex flex-col gap-2">
                        {SUGGESTED_QUESTIONS.map((question, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleSend(question)}
                                className="text-left text-sm text-purple-700 bg-white hover:bg-purple-50 border border-slate-200 hover:border-purple-200 px-4 py-3 rounded-xl transition-all shadow-sm active:scale-95 flex items-center justify-between group"
                            >
                                <span>{question}</span>
                                <Send size={14} className="opacity-0 group-hover:opacity-100 text-purple-500 transition-opacity" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
